/* eslint-disable complexity */
import {
  Button,
  Icon,
  MIN_SUPPORTED_WIDTH,
  TooltipTrigger,
} from '@toggle/design-system';
import { useScrollListener, useWindowResize } from '@toggle/helpers';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { useWindowSize } from '~/hooks/UseWindowSize';
import { useScheduleDrawerUrlSync } from '~/hooks/use-schedule-drawer-url-sync/useScheduleDrawerUrlSync';
import { useListScheduledQueries } from '~/react-query-hooks/alfred/useScheduledQueries';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '~/stores/use-flags/useFlags';
import {
  CategoryType,
  useScheduledTaskView,
} from '~/stores/use-scheduled-task-view/useScheduledTaskView';
import { NewChatButton } from '~/views/alfred/new-chat-button/NewChatButton';
import { useThinkingStepsSidebar } from '~/views/alfred/research/hooks/useThinkingStepsSidebar';

import { HistoryGroup, orderedGroupTypes } from '../history-group/HistoryGroup';
import { HistoryGroupLoader } from '../history-group-loader/HistoryGroupLoader';
import { useFetchInfiniteAllConversations } from '../hooks/useFetchInfiniteConversation';
import { useReasonerParams } from '../hooks/useReasonerParams';
import { useScheduledTaskRunSync } from '../hooks/useScheduledTaskRunSync';
import { SearchChat } from '../search-chat/SearchChat';
import { useChat } from '../use-chat/useChat';
import { CategoryTabs } from './CategoryTabs';
import styles from './HistorySidebar.module.scss';
import { ScheduledTaskList } from './ScheduledTaskList';
import { groupConversations } from './utils/history-sidebar-utils';

export type { CategoryType } from '~/stores/use-scheduled-task-view/useScheduledTaskView';

export const MAX_SCHEDULED_TASKS = 3;

interface HistorySidebarProps {
  isHistoryExpanded: boolean;
  toggleHistoryTab: () => void;
  openSharingModal: () => void;
}

export const HistorySidebar = ({
  isHistoryExpanded,
  toggleHistoryTab,
  openSharingModal,
}: HistorySidebarProps) => {
  const contentSectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(['chat', 'common']);
  const { trackAlfredChatSearchOpen } = usePosthogEvents();
  const [isSearchOpened, setSearchOpen] = useState(false);
  const {
    category,
    setCategory,
    setSelectedScheduledQueryId,
    setPendingRunTaskId,
  } = useScheduledTaskView(state => ({
    category: state.category,
    setCategory: state.setCategory,
    setSelectedScheduledQueryId: state.setSelectedScheduledQueryId,
    setPendingRunTaskId: state.setPendingRunTaskId,
  }));
  const { conversations, clearActiveConversation, setNewConversationOrigin } =
    useChat(state => ({
      conversations: state.conversations,
      clearActiveConversation: state.clearActiveConversation,
      setNewConversationOrigin: state.setNewConversationOrigin,
    }));

  const { windowWidth } = useWindowSize();
  const isLsegTouchDevice = windowWidth < MIN_SUPPORTED_WIDTH;

  const { deleteReasonerConversationId } = useReasonerParams();
  const { resetSidebar } = useThinkingStepsSidebar();

  const { open: openScheduleDrawer } = useScheduleDrawerUrlSync();
  const webSearch = useChat(state => state.webSearch);

  const isScheduledTaskEnabled = useFlags(state =>
    state.getFeatureFlagState('scheduled_task_feature')
  );

  useScheduledTaskRunSync();

  const { data: scheduledQueriesData, isLoading: isScheduledQueriesLoading } =
    useListScheduledQueries({ status: 'active' }, isScheduledTaskEnabled);
  const scheduledTasksCount = scheduledQueriesData?.total_count ?? 0;
  // Treat an unknown count (still loading) as at the limit to avoid a first-load
  // window where the button is enabled for users already at the cap.
  const isScheduleButtonDisabled =
    isScheduledTaskEnabled &&
    (isScheduledQueriesLoading || scheduledTasksCount >= MAX_SCHEDULED_TASKS);

  const handleCategoryChange = (newCategory: CategoryType) => {
    setCategory(newCategory);

    if (newCategory !== 'scheduled') {
      setSelectedScheduledQueryId(null);
    }
  };

  const handleNewConversation = () => {
    setNewConversationOrigin('side_menu');
    deleteReasonerConversationId();
    clearActiveConversation();
    resetSidebar();
    setSelectedScheduledQueryId(null);
    setCategory('all');
    setPendingRunTaskId(null);
  };

  const { height, scrollHeight } = useWindowResize({
    targetRef: contentSectionRef,
  });
  const isOverflowY = scrollHeight > height;

  const starredConversations = conversations
    .filter(({ favourite }) => favourite)
    .sort((a, b) => (a.date > b.date ? -1 : 1));
  const groups = groupConversations(
    conversations.sort((a, b) => (a.date > b.date ? -1 : 1))
  );

  const groupKeys = orderedGroupTypes.filter(
    groupType => groups[groupType]?.length > 0
  );

  //TODO: will kill this next and move everything to store
  const { isLoading, hasNextPage, fetchNextPage } =
    useFetchInfiniteAllConversations();

  useScrollListener(
    contentSectionRef,
    () => {
      if (hasNextPage) {
        fetchNextPage();
      }

      return hasNextPage;
    },
    [hasNextPage, isOverflowY],
    { isGlobalScrollListened: false, offset: 20 }
  );

  useEffect(() => {
    if (!isOverflowY && hasNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [isOverflowY, hasNextPage, isLoading, fetchNextPage]);

  return (
    <div
      className={clsx(
        styles.root,
        isHistoryExpanded ? styles.rootExpanded : styles.rootCollapsed
      )}
    >
      <div className={styles.toggleButtonWrapper}>
        {isHistoryExpanded && (
          <header className={styles.header}>
            {t('common:sideNavigation.alfred')}
          </header>
        )}
        <div className={styles.leftSection}>
          <TooltipContainer
            disabled={isLsegTouchDevice}
            label={
              isHistoryExpanded
                ? t('common:sideNavigation.hideMenu')
                : t('common:sideNavigation.showMenu')
            }
            trigger={TooltipTrigger.Hover}
            placement="right"
          >
            <button
              type="button"
              className={clsx(styles.toggleButton, {
                [styles.toggleButtonExpanded]: isHistoryExpanded,
              })}
              onClick={toggleHistoryTab}
              aria-label={
                isHistoryExpanded
                  ? t('common:sideNavigation.hideMenu')
                  : t('common:sideNavigation.showMenu')
              }
              data-testid={'button-toggle'}
            >
              <Icon
                iconName={!isHistoryExpanded ? 'HamburgerMenu' : 'ArrowRight'}
                size={16}
              />
            </button>
          </TooltipContainer>
        </div>
      </div>
      <div
        className={clsx(
          styles.headerSection,
          isHistoryExpanded
            ? styles.headerSectionExpanded
            : styles.headerSectionCollapsed
        )}
        onClick={() => {
          isLsegTouchDevice && toggleHistoryTab();
        }}
      >
        <div
          className={clsx(
            styles.buttonsWrapper,
            isHistoryExpanded
              ? styles.buttonsWrapperExpanded
              : styles.buttonsWrapperCollapsed
          )}
          onClick={e => e.stopPropagation()}
        >
          <button
            type="button"
            className={styles.buttonWrapper}
            onClick={handleNewConversation}
            aria-label={t('chat:historySidebar.newConversation')}
          >
            <NewChatButton iconOnly isHistoryExpanded={isHistoryExpanded} />
            {isHistoryExpanded && t('chat:historySidebar.newConversation')}
          </button>
          <button
            type="button"
            className={styles.buttonWrapper}
            aria-label={t('chat:historySidebar.searchConversation')}
            onClick={() => {
              if (!isSearchOpened) {
                trackAlfredChatSearchOpen(POSTHOG_TRACKING_SOURCES.ALFRED);
              }

              setSearchOpen(!isSearchOpened);
            }}
          >
            <SearchChat
              isHistoryExpanded={isHistoryExpanded}
              setIsOpen={setSearchOpen}
              isOpen={isSearchOpened}
            />
            {isHistoryExpanded && t('chat:historySidebar.searchConversation')}
          </button>
          {isScheduledTaskEnabled && (
            <TooltipContainer
              label={
                isScheduleButtonDisabled
                  ? t('chat:historySidebar.scheduleTaskDisabledMaxReached')
                  : t('chat:historySidebar.scheduleTask')
              }
              inPortal
              trigger={[TooltipTrigger.Hover]}
            >
              <div
                className={clsx(styles.buttonWrapper, {
                  [styles.buttonWrapperDisabled]: isScheduleButtonDisabled,
                })}
                onClick={() => {
                  if (isScheduleButtonDisabled) {
                    return;
                  }

                  openScheduleDrawer(undefined, webSearch);
                }}
                data-testid="schedule-task-button-wrapper"
              >
                <Button
                  variant="tertiary"
                  size="small"
                  iconName="Clock"
                  aria-label={t('chat:historySidebar.scheduleTask')}
                  disabled={isScheduleButtonDisabled}
                />
                {isHistoryExpanded && (
                  <>
                    {t('chat:historySidebar.scheduleTask')}
                    <span className={styles.scheduleCount}>
                      {scheduledTasksCount}/{MAX_SCHEDULED_TASKS}
                    </span>
                  </>
                )}
              </div>
            </TooltipContainer>
          )}
        </div>
      </div>

      {isHistoryExpanded && (
        <>
          <CategoryTabs category={category} changeCategory={handleCategoryChange} />
          <div
            className={styles.contentSection}
            ref={contentSectionRef}
            onClick={() => {
              isLsegTouchDevice && toggleHistoryTab();
            }}
          >
            {category === 'bookmarked' && (
              <HistoryGroup
                groupType="starred"
                conversations={starredConversations}
                category={category}
                handleOpenSharingModal={openSharingModal}
              />
            )}
            {isScheduledTaskEnabled && category === 'scheduled' && (
              <ScheduledTaskList />
            )}
            {category === 'all' && (
              <>
                {groupKeys.map(groupType => (
                  <HistoryGroup
                    key={groupType}
                    groupType={groupType}
                    conversations={groups[groupType]}
                    handleOpenSharingModal={openSharingModal}
                    category={category}
                  />
                ))}
              </>
            )}
            {isLoading && <HistoryGroupLoader />}
          </div>
        </>
      )}
    </div>
  );
};
